import React from "react";
import { Factory, Lock, FileCheck2, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatINR } from "@/lib/api";
import {
  STATUS_LABELS, STYLE, PAYMENT_STYLE, PAYMENT_LABELS,
  GOLD_STATUS_LABELS, GOLD_STATUS_STYLE, RUPEE_STATUS_LABELS, RUPEE_STATUS_STYLE,
} from "@/pages/admin/orders/orderConstants";

// Base (pre-discount/markup) average diamond rate for a line item — Admin-only visibility.
// The customer-facing invoice/PDP only ever shows the blended `effective_rate_per_carat`.
const baseDiamondRate = (snapshot) => {
  const diamonds = snapshot?.diamonds || [];
  const wt = diamonds.reduce((s, d) => s + (Number(d.carat) || 0), 0);
  const val = diamonds.reduce((s, d) => s + (Number(d.value) || 0), 0);
  return wt > 0 ? val / wt : 0;
};

export const ViewOrderSheet = ({ viewFor, onClose, onGenerateInvoice, busy }) => (
  <Sheet open={!!viewFor} onOpenChange={(o) => !o && onClose()}>
    <SheetContent data-testid="view-order-sheet" className="sm:max-w-lg overflow-y-auto">
      <SheetHeader><SheetTitle>Order {viewFor?.order_no}</SheetTitle></SheetHeader>
      {viewFor && (
        <div className="mt-4 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-muted-foreground text-xs">Customer</span><div>{viewFor.shop_name}</div></div>
            <div><span className="text-muted-foreground text-xs">Mobile</span><div className="font-mono">{viewFor.shop_phone}</div></div>
            <div><span className="text-muted-foreground text-xs">Salesman</span><div>{viewFor.salesman_name || "Unassigned"}</div></div>
            <div><span className="text-muted-foreground text-xs">Order Date</span><div>{new Date(viewFor.created_at).toLocaleString("en-IN")}</div></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={STYLE[viewFor.status]}>{STATUS_LABELS[viewFor.status]}</Badge>
            {viewFor.invoice_no ? (
              <>
                <Badge className={GOLD_STATUS_STYLE[viewFor.gold_status]}>{GOLD_STATUS_LABELS[viewFor.gold_status]}</Badge>
                <Badge className={RUPEE_STATUS_STYLE[viewFor.rupee_status]}>Rupee: {RUPEE_STATUS_LABELS[viewFor.rupee_status]}</Badge>
                {viewFor.is_overdue && <Badge className={PAYMENT_STYLE.overdue}>Overdue ({viewFor.days_overdue}d)</Badge>}
                {viewFor.fully_settled && <Badge className={`${PAYMENT_STYLE.paid} gap-1`}><CheckCircle2 className="h-3 w-3" /> Fully Settled</Badge>}
              </>
            ) : (
              <>
                <Badge className={PAYMENT_STYLE.not_invoiced}>Not Invoiced</Badge>
                {!["rejected", "cancelled", "completed"].includes(viewFor.status) && (
                  <Button data-testid="view-sheet-generate-invoice-button" size="sm" className="gap-1.5 h-7" disabled={busy} onClick={() => onGenerateInvoice(viewFor)}>
                    <FileCheck2 className="h-3.5 w-3.5" /> Generate Invoice
                  </Button>
                )}
              </>
            )}
          </div>
          <div className="border border-border rounded-lg divide-y divide-border">
            {(viewFor.items || []).map((it) => {
              const rate = baseDiamondRate(it.pricing_snapshot);
              return (
                <div key={it.product_id} className="flex items-center justify-between px-3 py-2">
                  <div className="min-w-0 flex items-center gap-2">
                    <div>
                      <div className="truncate flex items-center gap-1.5">
                        {it.name}
                        {it.fulfillment_type === "production_request" && (
                          <Badge data-testid="order-item-production-badge" className="bg-[hsl(var(--gold-light))] text-[hsl(var(--gold-dark))] border border-[hsl(var(--gold-dark))]/30 text-[10px] gap-1"><Factory className="h-3 w-3" /> Make to Order</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground font-mono">{it.sku} · Qty {it.quantity}</div>
                      {rate > 0 && (
                        <div data-testid="order-item-base-diamond-rate" className="text-[11px] text-muted-foreground font-mono mt-0.5">Base diamond rate: {formatINR(rate)}/ct</div>
                      )}
                    </div>
                  </div>
                  <span className="font-mono">{formatINR(it.line_total)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between font-medium"><span>Total</span><span className="font-mono">{formatINR(viewFor.total)}</span></div>

          {/* Admin-only — the customer invoice/PDF and storefront never show this. */}
          {!!viewFor.shop_discount_pct && (
            <div data-testid="order-internal-pricing" className="rounded-lg border border-dashed border-[hsl(var(--gold-dark))]/40 bg-[#F7F0E6] p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
                <Lock className="h-3 w-3" /> Internal Pricing (Admin Only)
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">List Amount (base)</span>
                  <div className="font-mono">{formatINR(viewFor.list_subtotal ?? viewFor.total)}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">{viewFor.shop_discount_pct > 0 ? "Discount Tier" : "Markup Tier"}</span>
                  <div className="font-mono">{Math.abs(viewFor.shop_discount_pct)}%</div>
                </div>
                {viewFor.diamond_value_before_discount > 0 && (
                  <div>
                    <span className="text-muted-foreground">Diamond Value (base)</span>
                    <div className="font-mono">{formatINR(viewFor.diamond_value_before_discount)}</div>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">{(viewFor.discount_amount || 0) >= 0 ? "Discount Amount" : "Markup Amount"}</span>
                  <div className="font-mono">{formatINR(Math.abs(viewFor.discount_amount || 0))}</div>
                </div>
              </div>
            </div>
          )}

          {viewFor.status === "rejected" && viewFor.rejection_reason && (
            <div className="text-xs text-[#7A1F1F] bg-[#FBE7E7] rounded-lg p-2">Rejection reason: {viewFor.rejection_reason}</div>
          )}
          {viewFor.shipping?.tracking_number && (
            <div className="text-xs text-muted-foreground">Shipped via {viewFor.shipping.courier_name || "courier"} · Tracking: {viewFor.shipping.tracking_number}</div>
          )}
        </div>
      )}
    </SheetContent>
  </Sheet>
);
